import type { Service, User } from "@/types";
import type { SupportTicketCategory } from "@/types/supportTickets";

// export interface Participant {
// 	onlineStatus: UserOnlineStatus;
// 	_id: string;
// 	firstName: string;
// 	lastName: string;
// 	email: string;
// 	avatar?: string;
// 	bio?: string;
// 	stripeSubscriptionId: string | null;
// }

export type Participant = Pick<
	User,
	"_id" | "firstName" | "lastName" | "email" | "avatar" | "bio" | "onlineStatus" | "plan" | "role" | "companyName"
> & {
	deletedAccount?: boolean;
};

export enum UserOnlineStatus {
	ONLINE = "ONLINE",
	OFFLINE = "OFFLINE",
	AWAY = "AWAY",
	BUSY = "BUSY",
	OUT_OF_OFFICE = "OUT_OF_OFFICE",
	ON_ANNUAL_LEAVE = "ON_ANNUAL_LEAVE",
}

export const USER_ONLINE_STATUS_MAPPING: Record<UserOnlineStatus, string> = {
	[UserOnlineStatus.ONLINE]: "Online",
	[UserOnlineStatus.AWAY]: "Away",
	[UserOnlineStatus.BUSY]: "Busy",
	[UserOnlineStatus.OFFLINE]: "Offline",
	[UserOnlineStatus.ON_ANNUAL_LEAVE]: "On Annual Leave",
	[UserOnlineStatus.OUT_OF_OFFICE]: "Out of Office",
};

export interface Chat {
	_id: string;
	participants: Participant[];
	chatType?: "DIRECT" | "CLIENT_SUPPORT_GROUP" | "CUSTOM_GROUP";
	supportGroupUserId?: string | null;
	groupName?: string;
	groupAvatar?: string;
	groupKey?: string;
	lauraPaused?: boolean;
	lauraPausedAt?: string | null;
	lauraPausedByUserId?: string | null;
	lauraResumedAt?: string | null;
	lauraResumedByUserId?: string | null;
	createdAt: string;
	updatedAt: string;
	__v?: number;
	id?: string;
	deletedFor?: string[];
	participantSnapshots?: {
		userId: string;
		firstName?: string;
		lastName?: string;
		email?: string;
		avatar?: string;
		role?: string;
		companyName?: string;
	}[];
	lastMessage?: ChatMessage | null;
	unreadCount?: number;
	clientSupportCustomerUnreadCount?: number;
	clientSupportStaffUnreadCount?: number;
}

export type CreateChatBody = { participants: string[] };
export type CreateGroupChatBody = {
	groupName: string;
	groupAvatar?: string;
	participants: string[];
};

export type ClientSupportGroupMembers = {
	directors: Participant[];
	admins: Participant[];
	availableAdmins: Participant[];
	members: Participant[];
	adminMemberIds: string[];
};

export interface ChatApiResponse {
	docs: Chat[];
	totalDocs: number;
	limit: number;
	totalPages: number;
	page: number;
	pagingCounter: number;
	hasPrevPage: boolean;
	hasNextPage: boolean;
	prevPage: null;
	nextPage: null;
}

export interface ChatMessage {
	_id: string;
	content: string;
	senderId: Participant | null;
	senderType?: "USER" | "AI";
	senderDisplayName?: string;
	senderAvatar?: string;
	clientSupportSenderRole?: "CUSTOMER" | "STAFF" | "";
	chatId: string;
	replyToMessageId: string | ReplyToMessagePreview | null;
	isRead: boolean;
	readByUserIds?: string[];
	fileUrls: string[];
	catalogSuggestions?: CatalogSuggestion[];
	supportTicketDraft?: SupportTicketDraft | null;
	aiKnowledgeDraft?: AiKnowledgeDraft | null;
	createdAt: string;
	updatedAt: string;
	__v: number;
	id: string;
}

export type CatalogSuggestion = {
	type: "service" | "blog";
	title: string;
	label?: string;
	description?: string;
	url: string;
	imageUrl?: string;
	meta?: string;
	tags?: string[];
	service?: Service | null;
};

export type ReplyToMessagePreview = {
	_id: string;
	content: string;
	senderType?: "USER" | "AI";
	senderDisplayName?: string;
};

export type SupportTicketDraft = {
	category: SupportTicketCategory;
	subject: string;
	description: string;
	status?: "pending" | "cancelled" | "submitted";
	ticketId?: string;
};

export type AiKnowledgeDraft = {
	knowledgeId: string;
	title: string;
	content: string;
	tags: string[];
	entryType?: "KNOWLEDGE" | "RULE";
	status?: "pending" | "accepted" | "rejected";
};

export interface ChatMessageApiResponse {
	docs: ChatMessage[];
	totalDocs: number;
	limit: number;
	totalPages: number;
	page: number;
	pagingCounter: number;
	hasPrevPage: boolean;
	hasNextPage: boolean;
	prevPage: null;
	nextPage: null;
}

export type Message = {
	isReceived: boolean;
	senderName?: string;
	senderRole?: string;
	senderType?: "USER" | "AI";
	clientSupportSenderRole?: "CUSTOMER" | "STAFF" | "";
	content: string;
	createdAt: string;
	fileUrls: string[];
	catalogSuggestions?: CatalogSuggestion[];
	supportTicketDraft?: SupportTicketDraft | null;
	aiKnowledgeDraft?: AiKnowledgeDraft | null;
	replyToMessage?: ReplyToMessagePreview | null;
	isRead?: boolean;
	_id?: string; // Message ID for scrolling
};
